import { Box, TextField, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import { PlanValues } from 'api/elplan/elplan.types'
import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Datetime from 'utils/datetime/datetime'
import { parseDecimalInput } from 'utils/stringUtils'

import styles from './TimeEffectTable.module.less'

export interface TimeEffectTableProps {
  currentPlanValues?: PlanValues[]
  onCellChange: (value: number, index: number) => void
}

export default function TimeEffectTable({ currentPlanValues = [], onCellChange }: TimeEffectTableProps): ReactElement {
  const { t } = useTranslation()

  const [planValues, setPlanValues] = useState(currentPlanValues.map((value) => `${value.volume}`))

  const halfOfHourValues = Math.ceil(planValues.length / 2)

  useEffect(() => {
    setPlanValues(currentPlanValues.map((value) => `${value.volume}`))
  }, [currentPlanValues])

  function renderRow(value: PlanValues, index: number) {
    const startLabel = Datetime.toLocalTime(value.start_time, 'hour')
    const endLabel = Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(value.start_time), 'hour')
    const range = `${startLabel}-${endLabel}`

    return (
      <Grid
        key={index}
        container
        wrap="nowrap"
        alignItems="center"
        className={styles.TimeEffectTable_Row}
        data-testid="time-effect-row"
        spacing={1}
      >
        <Grid className={styles.TimeEffectTable_Time}>
          <Typography variant="body2" fontWeight={600} component="span">
            {range}
          </Typography>
        </Grid>
        <Grid flexGrow={1} className={styles.TimeEffectTable_InputWrapper}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            aria-label={t('Volume for period {{range}}', { range })}
            value={planValues[index]}
            onBlur={(e) => {
              const rawValue = e.target.value
              if (rawValue === '') {
                onCellChange(0, index)
                return
              }
              const newValue = parseDecimalInput(rawValue)
              if (!isNaN(newValue)) onCellChange(newValue, index)
            }}
            onChange={(e) => {
              const updated = [...planValues]
              updated[index] = e.target.value
              setPlanValues(updated)
            }}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
            }}
          />
        </Grid>
      </Grid>
    )
  }

  const left = currentPlanValues.slice(0, halfOfHourValues)
  const right = currentPlanValues.slice(halfOfHourValues)

  return (
    <Box className={styles.TimeEffectTable}>
      <Typography variant="caption" className={styles.TimeEffectTable_Header}>
        {t('Volume')} (MW)
      </Typography>
      <Grid container spacing={2} wrap="nowrap">
        <Grid container className={styles.TimeEffectTable_Column} size={{ xs: 12, md: 6 }}>
          {left.map((v, i) => renderRow(v, i))}
        </Grid>
        <Grid container className={styles.TimeEffectTable_Column} size={{ xs: 12, md: 6 }}>
          {right.map((v, i) => renderRow(v, i + left.length))}
        </Grid>
      </Grid>
    </Box>
  )
}
